<template>
    <Layout :title="$t('profile.title')">
        <Spinner v-if="loading" />
        <div class="content__title-wrap">
            <h1 class="content__title">{{ $t('profile.title') }}</h1>
        </div>
        <div class="account-edit__tabs">
            <button
                v-for="tab in tabs"
                :key="tab"
                v-t="`profile.tabs.${tab}`"
                :class="{ 'is-active': tab === selectedTab }"
                @click.prevent="selectTab(tab)"
            />
        </div>

        <template v-if="selectedTab === 'profile'">
            <form class="account-edit__form" @submit.prevent="save">
                <PhotoInput
                    class="account-edit__photo"
                    :photo="localUser.photo"
                    @selected="onPictureSelected"
                    @deleted="onPictureDeleted"
                />
                <p
                    v-if="!$store.getters.personalDataFilled"
                    class="account-edit__notice"
                    v-html="$t('profile.emptyData')"
                ></p>

                <Spinner v-if="loading" />
                <h2 class="form-group__title">{{ $t('profile.personalData') }}</h2>
                <div class="form-group flex">
                    <FormInput
                        v-model="localUser.last_name"
                        class="account-edit__input"
                        type="text"
                        :placeholder="$t('misc.lastName') + '*'"
                        :error="dataError && dataError['last_name'] ? dataError['last_name'] : null"
                    />
                    <FormInput
                        v-model="localUser.first_name"
                        class="account-edit__input"
                        type="text"
                        :placeholder="$t('misc.firstName') + '*'"
                        required
                        :error="
                            dataError && dataError['first_name'] ? dataError['first_name'] : null
                        "
                    />
                    <FormInput
                        v-if="!$store.getters.isAdmin"
                        v-model="localUser.patronymic"
                        class="account-edit__input"
                        type="text"
                        :placeholder="$t('misc.patronymic')"
                    />
                </div>
                <div class="form-group">
                    <Checkbox v-model="localUser.user_policy">
                        {{ $t('profile.iAgreeWith') }}&nbsp;
                        <router-link to="/docs/agreement" class="link">
                            {{ $t('profile.termsOfService') }}
                        </router-link>
                    </Checkbox>
                </div>
                <button type="submit" class="button button--big">{{ $t('misc.save') }}</button>
                <ErrorList
                    :error="
                        dataError && dataError['non_field_errors']
                            ? dataError['non_field_errors']
                            : null
                    "
                />
            </form>
        </template>
        <template v-if="selectedTab === 'pseudonymes'">
            <template v-if="!$store.getters.isAdmin">
                <h2 class="form-group__title">{{ $t('misc.pseudonymes') }}</h2>
                <AuthorPseudonymes class="account-edit__pseudonymes" />
            </template>
        </template>
        <template v-if="selectedTab === 'security'">
            <form class="account-edit__form" @submit.prevent="resetPassword">
                <Spinner v-if="passLoading" />
                <h2 class="form-group__title">{{ $t('profile.security') }}</h2>
                <div class="form-group">
                    <div class="form-group__label">{{ $t('profile.changePassword') }}</div>
                    <FormInput
                        v-model="password1"
                        class="account-edit__input"
                        type="password"
                        :placeholder="$t('profile.newPass')"
                        required
                        :error="
                            passError && passError['new_password1']
                                ? passError['new_password1']
                                : null
                        "
                    />
                    <FormInput
                        v-model="password2"
                        class="account-edit__input"
                        type="password"
                        :placeholder="$t('profile.confirmPass')"
                        required
                        :error="
                            passError && passError['new_password2']
                                ? passError['new_password2']
                                : null
                        "
                    />
                </div>
                <button type="submit" class="button button--big">
                    {{ $t('profile.changePassword') }}
                </button>
            </form>
        </template>
        <template
            v-if="selectedTab === 'export' && ($store.getters.isAdmin || $store.getters.isStaff)"
        >
            <div class="form-group">
                <h2 v-t="'profile.export'" class="form-group__title" />
                <button
                    v-t="'profile.exportingUsers'"
                    type="button"
                    class="button button--link"
                    style="margin-right: 15px;"
                    @click="exportUsers"
                />
                <button
                    v-t="'profile.exportingBooks'"
                    type="button"
                    class="button button--link"
                    @click="exportBooks"
                />
            </div>
        </template>
        <template v-if="selectedTab === 'projects'">
            <PaginatedResource :fetch-method="getСustomersMembership">
                <template #default="{ loading, resource, canLoadMore, loadMore, isEmpty }">
                    <div class="form-group account-edit__projects">
                        <Spinner v-if="loading" />
                        <table class="control-table">
                            <thead class="control-table__head">
                                <tr class="control-table__row">
                                    <th class="control-table__cell">
                                        <span v-t="'crm.users.list.customer'" />
                                    </th>
                                    <th class="control-table__cell">
                                        <span v-t="'crm.users.list.role'" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="control-table__body">
                                <tr v-if="!loading && isEmpty" class="control-table__row">
                                    <td class="control-table__cell">
                                        <span v-t="'crm.users.list.empty'" />
                                    </td>
                                </tr>
                                <tr
                                    v-for="item in resource"
                                    :key="item.id"
                                    class="control-table__row"
                                >
                                    <td class="control-table__cell">
                                        {{ item.customer }}
                                    </td>
                                    <td class="control-table__cell">
                                        <div class="account-edit__projects-labels">
                                            <div
                                                v-for="roleLabel in item.role.map(
                                                    ({ label }) => label
                                                )"
                                                :key="roleLabel"
                                                v-text="roleLabel"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-if="canLoadMore">
                            <button
                                v-t="'misc.loadmore'"
                                class="button button--big button--blank"
                                type="button"
                                @click="loadMore"
                            />
                        </div>
                    </div>
                </template>
            </PaginatedResource>
        </template>
    </Layout>
</template>

<script>
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import { updateUser, changePassword, exportUsers } from '../../api';
import {
    getСustomersMembersMembership,
    getCustomersEmployeesMembership
} from '@speechki-frontend/crm/src/customers/api/customers';
import { FormInput, PhotoInput, ErrorList, Checkbox } from '@speechki-frontend/shared/ui/forms';
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';
import { AuthorPseudonymes } from '../../components';

import { exportBooks } from '@speechki-frontend/core/editor/api/books';

import { mapState } from 'vuex';

export default {
    name: 'AccountEdit',
    components: {
        Layout,
        PhotoInput,
        ErrorList,
        FormInput,
        Checkbox,
        PaginatedResource,
        AuthorPseudonymes
    },
    data() {
        return {
            localUser: {},
            profilePhoto: true,
            citizenship: 'russia',
            password1: '',
            password2: '',
            confirm: false,
            loading: false,
            passLoading: false,
            dataError: null,
            passError: null,
            tabs: ['profile', 'pseudonymes', 'security'],
            selectedTab: 'profile'
        };
    },
    computed: {
        ...mapState({
            user: state => state.auth.user,
            authors: state => state.auth.userAuthors
        })
    },
    watch: {
        user: {
            immediate: true,
            handler() {
                if (this.user && this.user.pk) {
                    let birth_day = null;
                    if (this.user.birth_day) {
                        birth_day = new Date(this.user.birth_day);
                    }
                    this.localUser = { ...this.user, birth_day };

                    if (!this.localUser.citizenship || this.localUser.citizenship === 'Россия') {
                        this.citizenship = 'russia';
                        this.localUser.citizenship = 'Россия';
                    } else {
                        this.citizenship = 'other';
                    }
                }
            }
        },
        citizenship() {
            if (this.citizenship === 'russia') {
                this.localUser.citizenship = 'Россия';
            } else {
                if (!this.user.citizenship) {
                    this.localUser.citizenship = '';
                }
            }
        },
        selectedTab(tab) {
            if (tab === 'pseudonymes' && !this.authors.length && !this.$store.getters.isAdmin) {
                this.$store.dispatch('fetchUserAuthors');
            }
        }
    },
    async created() {
        if (this.$store.getters.isAdmin) {
            this.tabs.push('export');
        }
        if (this.$isCrmApp) {
            this.tabs.push('projects');
        }
    },
    methods: {
        async getСustomersMembership(params) {
            const req = await await getCustomersEmployeesMembership();
            const members = await await getСustomersMembersMembership(params);

            req.results = [...req.results, ...members.results];
            return req;
        },
        onPictureDeleted() {
            this.localUser.photo = '';
        },
        onPictureSelected(file) {
            this.localUser.photo = file;
        },
        async save() {
            try {
                this.dataError = null;
                this.loading = true;
                let data = new FormData();

                data.append('email', this.localUser.email || '');
                data.append('first_name', this.localUser.first_name || '');
                data.append('last_name', this.localUser.last_name || '');
                data.append('patronymic', this.localUser.patronymic || '');
                data.append('citizenship', this.localUser.citizenship || '');

                if (this.localUser.photo !== this.user.photo) {
                    data.append('photo', this.localUser.photo);
                }

                await updateUser(data);

                this.loading = false;
                this.$store.dispatch('fetchUser');
            } catch (error) {
                this.loading = false;
                this.dataError = error;
                console.log(error);
            }
        },
        formatDate(date) {
            const d = new Date(date);
            return `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${(
                '0' + d.getDate()
            ).slice(-2)}`;
        },
        async resetPassword() {
            try {
                this.passLoading = true;
                this.passError = null;
                await changePassword({
                    new_password1: this.password1,
                    new_password2: this.password2
                });
                this.passLoading = false;
                this.password1 = '';
                this.password2 = '';
            } catch (error) {
                this.passError = error;
                this.passLoading = false;
            }
        },
        async exportUsers() {
            try {
                this.loading = true;
                const response = await exportUsers();

                const url = window.URL.createObjectURL(new Blob([response]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'users.xlsx');
                document.body.appendChild(link);
                link.click();
                this.loading = false;
            } catch (e) {
                //
            }
        },
        async exportBooks() {
            try {
                this.loading = true;
                const response = await exportBooks();

                const url = window.URL.createObjectURL(new Blob([response]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'books.xlsx');
                document.body.appendChild(link);
                link.click();
                this.loading = false;
            } catch (e) {
                //
            }
        },
        selectTab(tab) {
            this.selectedTab = tab;
            // localStorage.setItem('profileNavigationTab', tab);
        }
    }
};
</script>

<style lang="scss" src="./ProfilePage.scss"></style>
